<script>
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import NestedQuote from '$lib/Components/Layout/NestedQuote/NestedQuote.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import noiseImage from './images/noise.svg';
	import thermalImage from './images/thermal-cropped-lower.png';
	import windImage from './images/wind.svg';

	import diagramImage from './images/exploded-diagram.svg';
</script>

<Section>
	<p class="text-left">
		Windows are a critical factor in your building's financial performance,
		often accounting for 30-40% of its heating and cooling costs and a
		significant portion of tenant or guest complaints.
	</p>
	<p class="text-left">
		IndowPRO is an engineered window upgrade that closes this performance gap.
		It delivers the benefits of a full replacement by drastically lowering
		energy bills and delivering higher tenant satisfaction, but without the high
		capital cost and disruptive installation. Access a faster, more significant
		return on investment with IndowPRO.
	</p>

	<h2 class="text-left">Third-Party Performance Data</h2>

	<div
		class="
    mb-12 grid gap-6
    md:grid-cols-3 md:gap-8
  "
	>
		<div>
			<p class="m-0 text-left text-lg font-bold">Thermal Performance</p>
			<p class="mb-2 text-left font-medium">U-0.136 / R-7.4*</p>
			<p class="text-left">
				Our system's thermal performance was modeled using NFRC-standard THERM
				software.
			</p>
			<p class="text-left text-sm">
				* With R-16 VIG installed inside single-pane windows with thermally
				unbroken steel window frames, as modeled with NFRC-standard THERM
				software. IndowPRO with VIG can improve single-pane window performance
				by 85%.
			</p>
			<Image src={thermalImage} class="max-w-sm" />
		</div>
		<div>
			<p class="m-0 text-left text-lg font-bold">Acoustic Performance</p>
			<p class="mb-2 text-left font-medium">STC 51</p>
			<p class="text-left">
				Verified via ASTM E90 testing, IndowPRO is capable of creating an
				exceptionally quiet interior environment, a premium amenity that
				directly impacts guest satisfaction and tenant retention.
			</p>
			<Image src={noiseImage} class="w-40" />
		</div>
		<div>
			<p class="m-0 text-left text-lg font-bold">Air Infiltration</p>
			<p class="mb-2 text-left font-medium">&lt;0.01 CFM</p>
			<p class="text-left">
				A tight building envelope is key to efficiency. IndowPRO's airtight seal
				practically eliminates drafts and uncontrolled air leakage, a critical
				factor in reducing overall energy consumption.
			</p>
			<Image src={windImage} class="w-40" />
		</div>
	</div>

	<h2 class="text-left">High-Performance Windows Increase NOI</h2>

	<div
		class="
    mb-8 grid gap-6
    md:grid-cols-2 md:gap-12
  "
	>
		<div>
			<h3 class="text-left">Drastically Reduce Utility Bills</h3>
			<p class="text-left">
				Windows are responsible for up to 40% of a building's heating and
				cooling costs. IndowPRO’s performance upgrade provides substantial
				annual energy savings and reduces HVAC duty cycles, lowering maintenance
				costs and extending equipment lifespan.
			</p>
		</div>
		<div>
			<h3 class="text-left">Enhance the Tenant Experience</h3>
			<p class="text-left">
				A quiet, comfortable building is a valuable building. By eliminating
				exterior noise and uncomfortable drafts, IndowPRO creates a premium
				interior environment that helps you attract and retain high-value
				tenants, reduce costly turnover, and justify higher lease rates.
			</p>
		</div>
	</div>
	<NestedQuote
		class="bg-indow-grey-dark text-white"
		quote="We realized if we fixed our window problem, we could lease the space for market rate. The return on the investment has been outstanding."
		name="Mike Larkin"
		label="Elliot Associates"
	/>
</Section>

<Section>
	<h2 class="text-left">How IndowPRO Delivers Exceptional Performance</h2>
	<Image src={diagramImage} />
	<p class="text-left">
		IndowPRO blocks noise and air infiltration with a patent-pending system
		featuring Flexible Corner Seals which allow the frame to adapt to irregular
		window openings. The frame absorbs out-of-squareness and window bows inside
		the frame, allowing a sleek aesthetic even when the original windows have
		structural challenges.
	</p>
</Section>

<Section backgroundColor="blue">
	<h2 class="text-left">Calculate the ROI for Your Building</h2>

	<p class="text-left">
		Every building has unique windows and project goals. Let our team provide a
		complimentary consultation to review your existing windows, discuss energy
		and acoustic performance targets, and determine the best path forward for
		your property.
	</p>
	<Button href="/about-indow/commercial-sales"
		>Request a Performance and ROI consult</Button
	>
</Section>
